import {CaptionIconMini, CopyIcon, CrossIcon, InfoIcon, TagsIcon} from '../../ui-components/Iconpack';
import {encodeUrl, GetFileSize, TruncateTitle} from '../../utils/helper';
import React, {ChangeEvent, ReactNode, useEffect, useState} from 'react';
import {FileData} from '../../reducers/media';
import {toast} from 'react-toastify';
import {CommonSpinLoader} from '../../ui-components/loaders';
import tracker from '../../lib/analytics/tracker';
import {EventNames} from '../../lib/analytics/events';

interface ImageMetadataProps {
    fileData: FileData | undefined;
    showVersion: () => void;
    savedVersionUid: string | null;
    versionNumber?: string | null;
    onSaveTags: (tags: string[]) => void;
    onSaveCaption: (caption: string) => void;
}

export const ImageMetadata: React.FC<ImageMetadataProps> = ({
    fileData,
    showVersion,
    onSaveTags,
    onSaveCaption,
    savedVersionUid,
    versionNumber,
}) => {
    const hasImageVersions = Object.keys(fileData?.saved_images || {}).length;

    const [tags, setTags] = useState(fileData?.tags?.map((tag) => tag.text) ?? []);
    const [newTag, setNewTag] = useState('');
    const [tagsLoading, setTagsLoading] = useState(false);
    const [tagsEdited, setTagsEdited] = useState(false);
    const [caption, setCaption] = useState('');
    const [captionLoading, setCaptionLoading] = useState(false);
    const [captionEdited, setCaptionEdited] = useState(false);

    const handleAddTag = () => {
        if (newTag.length > 0 && !tags.includes(newTag)) {
            setTags([...tags, newTag]);
            setTagsEdited(true);
        }
        setNewTag('');
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags(tags.filter((tag) => tag !== tagToRemove));
        setTagsEdited(true);
    };

    const handleSaveTags = async () => {
        setTagsLoading(true);
        await onSaveTags(tags);
        setTagsLoading(false);
        setTagsEdited(false);
    };

    const handleCaptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setCaptionEdited(true);
        setCaption(e.target.value);
    };

    const handleSaveCaption = async () => {
        setCaptionEdited(false);
        setCaptionLoading(true);
        await onSaveCaption(caption);
        setCaptionLoading(false);
    };

    useEffect(() => {
        setCaption(fileData?.caption?.text ?? '');
        setTags(fileData?.tags?.map((tag) => tag.text) ?? []);
    }, [fileData]);

    const Btn = (props: {onClick: () => void; children?: ReactNode}) => {
        return (
            <div
                className='border-1 text-inter flex cursor-pointer justify-center rounded-2xl bg-blue-100 px-2 py-3 text-[14px] font-[700] text-[#1570EF]'
                onClick={props.onClick}>
                {props.children}
            </div>
        );
    };

    return (
        <div className='h-full w-full overflow-y-auto md:h-[100vh] md:w-[500px] md:bg-[#fafafb]'>
            <div className='flex flex-col gap-1 pb-6 pl-10 pt-5 font-[700]'>
                <div className='flex items-center gap-[6px]'>
                    <span className='text-[18px] text-[#0C111D]'>{TruncateTitle(fileData?.name, 15)}</span>
                    <div
                        className='cursor-pointer'
                        onClick={() => {
                            navigator.clipboard.writeText(encodeUrl(fileData?.cdn_url as string)).then(() => {
                                tracker.trackEvent(EventNames.IMAGE_COPIED);
                                toast.success('Image Url Copied!');
                            });
                        }}>
                        <CopyIcon width={18} height={18} color='#0C111D' />
                    </div>
                </div>
                {savedVersionUid && hasImageVersions > 0 && (
                    <span className='text-[14px] text-[#0C111D]' onClick={showVersion}>
                        Image Version No: #{versionNumber ? versionNumber : hasImageVersions}
                    </span>
                )}
                {hasImageVersions > 0 && (
                    <span className='cursor-pointer text-[14px] text-[#155EEF]' onClick={showVersion}>
                        {'View Other Image Versions >'}
                    </span>
                )}
            </div>
            <div className='border border-[#EAECF0]' />
            <div className='flex flex-col gap-4 pb-6 pl-10 pt-5 text-[16px] font-[400] text-[#0C111D]'>
                <div className='flex items-center gap-[6px]'>
                    <InfoIcon />
                    <span className='text-[18px] font-[700]'>File Info</span>
                </div>
                {savedVersionUid ? (
                    <>
                        <span className=''>
                            Dimensions:{' '}
                            <span className='font-[700]'>
                                {fileData?.saved_images[savedVersionUid].width}X
                                {fileData?.saved_images[savedVersionUid].height} px
                            </span>
                        </span>
                        <span className=''>
                            Image Size:{' '}
                            <span className='font-[700]'>
                                {GetFileSize(fileData?.saved_images[savedVersionUid].size)}
                            </span>
                        </span>
                        <span className=''>
                            Format:{' '}
                            <span className='font-[700]'>
                                {fileData?.saved_images[savedVersionUid].type.toUpperCase()}
                            </span>
                        </span>
                    </>
                ) : (
                    <>
                        <span className=''>
                            Dimensions:{' '}
                            <span className='font-[700]'>
                                {fileData?.width}X{fileData?.height} px
                            </span>
                        </span>
                        <span className=''>
                            Image Size: <span className='font-[700]'>{GetFileSize(fileData?.file_size)}</span>
                        </span>
                        <span className=''>
                            Format: <span className='font-[700]'>{fileData?.type.toUpperCase()}</span>
                        </span>
                    </>
                )}
            </div>
            <div className='border border-[#EAECF0]' />
            <div className={'pr-10'}>
                <div className='flex flex-col gap-4 pb-6 pl-10 pt-5 text-[16px] font-[400] text-[#0C111D]'>
                    <div className='flex justify-between'>
                        <div className={'flex items-center gap-2'}>
                            <TagsIcon />
                            <span className='text-[18px] font-[700]'>Tags</span>
                        </div>
                    </div>
                    {tagsLoading ? (
                        <div className={'flex justify-center py-10'}>
                            <CommonSpinLoader size={'xsm'} />
                        </div>
                    ) : (
                        <div className={'flex flex-wrap gap-1'}>
                            {tags.map((tag, index) => (
                                <div
                                    key={index}
                                    className='flex items-center rounded-[10px]
                                border-[1px] border-[#D0D5DD] bg-white px-2 py-1 font-[400] text-[#344054]'>
                                    <span className='mr-1'>{tag}</span>
                                    <button onClick={() => handleRemoveTag(tag)}>
                                        <CrossIcon />
                                    </button>
                                </div>
                            ))}
                            <div className='flex items-center'>
                                <input
                                    type='text'
                                    className='text-inter overflow-y-auto rounded border-none bg-white px-2 py-1 text-[14px] text-[#344054] outline-none md:bg-[#fafafb]'
                                    placeholder='+ new tag'
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value.trim() || '')}
                                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleAddTag()}
                                />
                            </div>
                        </div>
                    )}
                    {tagsEdited && !tagsLoading && (
                        <div>
                            <Btn onClick={handleSaveTags}>
                                <span className={'px-3 text-[14px] font-[700]'}>Save</span>
                            </Btn>
                        </div>
                    )}
                </div>
            </div>
            <div className='border border-[#EAECF0]' />
            <div className={'pr-10'}>
                <div className='flex flex-col gap-4 pb-6 pl-10 pt-5 text-[16px] font-[400] text-[#0C111D]'>
                    <div className='flex justify-between'>
                        <div className={'flex items-center gap-2'}>
                            <CaptionIconMini />
                            <span className='text-[18px] font-[700]'>Caption</span>
                        </div>
                    </div>
                    {captionLoading ? (
                        <div className={'flex justify-center py-10'}>
                            <CommonSpinLoader size={'xsm'} />
                        </div>
                    ) : (
                        <textarea
                            className='text-inter w-full resize-none rounded-2xl border-[1px] border-gray-200 bg-[#FFF] px-4 py-2 text-[16px] font-[400] text-[#344054] outline-none'
                            placeholder={'start typing...'}
                            value={caption ?? ''}
                            onChange={handleCaptionChange}
                            rows={4}
                        />
                    )}
                    {captionEdited && !captionLoading && (
                        <div>
                            <Btn onClick={handleSaveCaption}>
                                <span className={'px-3 text-[14px] font-[700]'}>Save</span>
                            </Btn>
                        </div>
                    )}
                </div>
            </div>
            <div className='border border-[#EAECF0]' />
        </div>
    );
};

export default ImageMetadata;
